import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Loader2, CalendarDays } from "lucide-react";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function eachNight(checkIn, checkOut) {
  const days = [];
  let cur = new Date(checkIn + "T00:00:00");
  const end = new Date(checkOut + "T00:00:00");
  while (cur < end) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

export default function CalendarPage() {
  const [props, setProps] = useState([]);
  const [selected, setSelected] = useState("");
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/properties").then(({ data }) => {
      setProps(data);
      if (data[0]) setSelected(data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api.get(`/properties/${selected}/availability`).then(({ data }) => setRanges(data)).finally(() => setLoading(false));
  }, [selected]);

  const bookedDays = useMemo(() => ranges.flatMap((r) => eachNight(r.check_in, r.check_out)), [ranges]);

  return (
    <div data-testid="calendar-page" className="max-w-5xl">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Availability</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-8">Reservation Calendar</h1>

      {props.length === 0 ? (
        <div className="text-center py-20 text-gray-400">Add a property to view its availability.</div>
      ) : (
        <>
          <div className="mb-8 max-w-sm">
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Property</label>
            <select data-testid="calendar-property-select" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1A365D] outline-none" value={selected} onChange={(e) => setSelected(e.target.value)}>
              {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm">
              {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1A365D]" size={26} /></div>
              ) : (
                <DayPicker
                  mode="single"
                  numberOfMonths={1}
                  modifiers={{ booked: bookedDays }}
                  modifiersClassNames={{ booked: "rdp-day_booked" }}
                  styles={{ day_today: { color: "#E07A5F", fontWeight: 700 } }}
                />
              )}
              <div className="flex items-center gap-5 mt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#E3D5CA]" /> Booked</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-[#E07A5F]" /> Today</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm">
              <h2 className="font-display text-2xl font-medium mb-4 flex items-center gap-2"><CalendarDays size={20} /> Booked Periods</h2>
              {ranges.length === 0 ? (
                <p className="text-gray-400 text-sm py-6 text-center">Fully available — no bookings yet.</p>
              ) : (
                <div className="divide-y divide-black/5">
                  {ranges.map((r, i) => (
                    <div key={i} data-testid={`booked-range-${i}`} className="py-3 flex items-center justify-between">
                      <span className="font-medium text-gray-800">{r.guest_name}</span>
                      <span className="text-sm text-gray-500">{fmt(r.check_in)} → {fmt(r.check_out)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
